import React, { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Filter,
  Search,
  History,
  Scale,
} from "lucide-react";

/**
 * Audit-trail palette — single source of truth so the drawer's per-step
 * logbook and the main Mutatie Historie panel share badge colours.
 */
export const ACTION_PALETTE = {
  "vng-toggle": {
    label: "VNG-stap",
    color: "#3CB371", // light green
    glow: "rgba(60,179,113,0.10)",
  },
  "evidence-note": {
    label: "Onderbouwing",
    color: "#00A3FF", // bright blue — same family as Document
    glow: "rgba(0,163,255,0.10)",
  },
  "evidence-document": {
    label: "Document",
    color: "#00A3FF",
    glow: "rgba(0,163,255,0.10)",
  },
  "role-toggle": {
    label: "Rol",
    color: "#8896A6", // soft anthracite/silver so it's readable on navy
    glow: "rgba(136,150,166,0.12)",
  },
  "role-name": {
    label: "Rol · naam",
    color: "#8896A6",
    glow: "rgba(136,150,166,0.12)",
  },
};

// ---------- Helpers ----------
const MONTHS_NL = [
  "jan", "feb", "mrt", "apr", "mei", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec",
];

export const formatAuditTs = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, "0");
  const month = MONTHS_NL[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${day} ${month} ${year}, ${hh}:${mm}:${ss}`;
};

const formatAuditDateOnly = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")} ${
    MONTHS_NL[d.getMonth()]
  } ${d.getFullYear()}`;
};

// Coarse-grained category — drives the filter dropdown + grouping key.
const CATEGORY_OF = {
  "vng-toggle": "vng",
  "evidence-note": "documenten",
  "evidence-document": "documenten",
  "role-toggle": "rollen",
  "role-name": "rollen",
};

const CATEGORY_LABEL_PLURAL = {
  vng: "VNG-stappen bevestigd",
  documenten: "Documenten bijgewerkt",
  rollen: "Projectrollen gewijzigd",
};

// Extract the step number ("01" – "08") from a log detail string like
// "Stap 03 · Ontwerp gemarkeerd als voltooid".  Returns null if not found.
const stepNumberOf = (entry) => {
  if (!entry || !entry.detail) return null;
  const m = entry.detail.match(/Stap\s+(\d{2})\b/);
  return m ? m[1] : null;
};

// ---------- AuditBadge — reused in the drawer too ----------
export const AuditBadge = ({ action }) => {
  const palette = ACTION_PALETTE[action] || {
    label: action,
    color: "#7FD7E4",
    glow: "rgba(127,215,228,0.08)",
  };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.22em] uppercase"
      style={{
        color: palette.color,
        border: `1px solid ${palette.color}55`,
        background: palette.glow,
      }}
      data-testid={`audit-badge-${action}`}
    >
      {palette.label}
    </span>
  );
};

// ---------- Grouping ----------
// Collapse consecutive entries that share the SAME category AND the same
// minute into a single group. The newest entry is shown as the parent
// representation; expanding the group reveals each individual entry.
const groupEntries = (entries) => {
  const groups = [];
  for (const entry of entries) {
    const cat = CATEGORY_OF[entry.action] || "overig";
    const minute = (entry.timestamp || "").slice(0, 16); // YYYY-MM-DDTHH:MM
    const last = groups[groups.length - 1];
    if (last && last.category === cat && last.minute === minute) {
      last.items.push(entry);
    } else {
      groups.push({
        category: cat,
        minute,
        items: [entry],
      });
    }
  }
  return groups;
};

// ---------- Filter pipeline ----------
const applyFilters = (logs, { query, action, step, dateFrom, dateTo }) => {
  const q = (query || "").trim().toLowerCase();
  const fromTs = dateFrom ? new Date(dateFrom).getTime() : null;
  // Make dateTo end-of-day for inclusivity
  const toTs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
  return logs.filter((e) => {
    if (q && !`${e.detail || ""}`.toLowerCase().includes(q)) return false;
    if (action && action !== "all") {
      if ((CATEGORY_OF[e.action] || "overig") !== action) return false;
    }
    if (step && step !== "all") {
      if (stepNumberOf(e) !== step) return false;
    }
    const ts = new Date(e.timestamp).getTime();
    if (fromTs !== null && ts < fromTs) return false;
    if (toTs !== null && ts > toTs) return false;
    return true;
  });
};

// ============================================================================
// AuditTrailPanel — full-blown audit dashboard for SystemResult.jsx
// ============================================================================
const PAGE_SIZE = 20;

export const AuditTrailPanel = ({ logs, onFilteredChange }) => {
  const [query, setQuery] = useState("");
  const [action, setAction] = useState("all");
  const [step, setStep] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [expanded, setExpanded] = useState({});

  // newest-first so the dashboard reads top-down chronologically
  const ordered = useMemo(() => [...logs].reverse(), [logs]);

  const filtered = useMemo(
    () => applyFilters(ordered, { query, action, step, dateFrom, dateTo }),
    [ordered, query, action, step, dateFrom, dateTo]
  );

  // Human-readable summary of active filters. `null` means no filters are
  // active and consumers (e.g. the PDF exporter) can render the full log.
  const filterSummary = useMemo(() => {
    const parts = [];
    const actionLabel = {
      documenten: "Documenten",
      rollen: "Rollen",
      vng: "VNG-status",
    }[action];
    if (actionLabel) parts.push(`Acties: ${actionLabel}`);
    if (step && step !== "all") parts.push(`Stap ${step}`);
    if (query.trim()) parts.push(`zoek: "${query.trim()}"`);
    if (dateFrom) parts.push(`vanaf ${dateFrom}`);
    if (dateTo) parts.push(`tot ${dateTo}`);
    return parts.length === 0 ? null : parts.join(" · ");
  }, [action, step, query, dateFrom, dateTo]);

  // Bubble the filtered list + summary up to the parent so the PDF export
  // can mirror exactly what the user is looking at.
  React.useEffect(() => {
    if (!onFilteredChange) return;
    // The hook always emits the chronological order matching the PDF: oldest
    // first. The UI itself flips this to newest-first for display only.
    onFilteredChange({
      logs: [...filtered].reverse(),
      summary: filterSummary,
    });
  }, [filtered, filterSummary, onFilteredChange]);

  const groups = useMemo(() => groupEntries(filtered), [filtered]);

  const visibleGroups = groups.slice(0, visibleCount);
  const hasMore = groups.length > visibleCount;

  const toggleGroup = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <section
      className="rk-container pb-24"
      data-testid="result-mutation-history"
    >
      <div className="hairline mb-12" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div className="max-w-3xl">
          <div
            className="overline mb-3 flex items-center gap-2"
            data-testid="result-mutation-eyebrow"
          >
            <History strokeWidth={1.25} className="w-3.5 h-3.5" />
            Mutatie historie · audit trail
          </div>
          <h2
            className="text-white tracking-tight leading-[1.1] text-3xl md:text-5xl"
            style={{ fontFamily: "Fraunces, serif", fontWeight: 300 }}
            data-testid="result-mutation-title"
          >
            Elke handeling{" "}
            <em
              className="italic text-[#D4D8DF]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              onuitwisbaar vastgelegd.
            </em>
          </h2>
          <p className="mt-4 text-sm text-[#9AA4B2] max-w-2xl leading-relaxed">
            Elke toets-stap, onderbouwing, document-koppeling en
            rolwijziging wordt automatisch toegevoegd aan deze append-only
            audit-trail.
          </p>
        </div>
        <div
          className="inline-flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.22em] uppercase shrink-0"
          data-testid="result-mutation-compliance"
          style={{
            color: "#7FD7E4",
            border: "1px solid rgba(127,215,228,0.40)",
            background: "rgba(127,215,228,0.06)",
          }}
        >
          <Scale strokeWidth={1.25} className="w-3.5 h-3.5" />
          Conform EU AI Act art. 12 · Traceability
        </div>
      </div>

      {/* Filter bar */}
      <div
        className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-8 p-4"
        data-testid="audit-filters"
        style={{
          background: "#0F1E33",
          border: "1px solid rgba(192,197,206,0.18)",
        }}
      >
        {/* Search */}
        <label className="md:col-span-4 relative block">
          <Search
            strokeWidth={1.25}
            className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#7FD7E4]"
            aria-hidden
          />
          <input
            type="text"
            placeholder="Zoek in logs…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setVisibleCount(PAGE_SIZE);
            }}
            data-testid="audit-filter-query"
            className="w-full bg-transparent text-white text-sm pl-9 pr-3 py-2 placeholder:text-[#6B7C95]"
            style={{
              border: "1px solid rgba(127,215,228,0.30)",
              outline: "none",
              fontFamily: "Outfit, sans-serif",
            }}
          />
        </label>
        <FilterSelect
          testid="audit-filter-action"
          value={action}
          onChange={(v) => {
            setAction(v);
            setVisibleCount(PAGE_SIZE);
          }}
          options={[
            { value: "all", label: "Alle acties" },
            { value: "documenten", label: "Documenten" },
            { value: "rollen", label: "Rollen" },
            { value: "vng", label: "VNG-status" },
          ]}
        />
        <FilterSelect
          testid="audit-filter-step"
          value={step}
          onChange={(v) => {
            setStep(v);
            setVisibleCount(PAGE_SIZE);
          }}
          options={[
            { value: "all", label: "Alle VNG-stappen" },
            ...["01", "02", "03", "04", "05", "06", "07", "08"].map((n) => ({
              value: n,
              label: `Stap ${n}`,
            })),
          ]}
        />
        <DateInput
          testid="audit-filter-date-from"
          value={dateFrom}
          onChange={(v) => {
            setDateFrom(v);
            setVisibleCount(PAGE_SIZE);
          }}
          placeholder="Vanaf"
        />
        <DateInput
          testid="audit-filter-date-to"
          value={dateTo}
          onChange={(v) => {
            setDateTo(v);
            setVisibleCount(PAGE_SIZE);
          }}
          placeholder="Tot"
        />
      </div>

      {/* Status strip */}
      <div
        className="flex items-center justify-between px-2 mb-3 text-[10px] tracking-[0.22em] uppercase"
        style={{ color: "#7FD7E4" }}
      >
        <span data-testid="audit-summary">
          {filtered.length} mutatie{filtered.length === 1 ? "" : "s"} ·{" "}
          {groups.length} groep{groups.length === 1 ? "" : "en"}
        </span>
        <span style={{ color: "#9AA4B2" }}>Append-only</span>
      </div>

      <div
        className="overflow-x-auto"
        data-testid="result-mutation-table"
        style={{
          background: "#162E4A",
          border: "1px solid rgba(192,197,206,0.20)",
          boxShadow: "0 30px 60px -30px rgba(0,0,0,0.55)",
        }}
      >
        <ul data-testid="result-mutation-list" className="min-w-[720px] md:min-w-0">
          {filtered.length === 0 && (
            <li
              className="px-6 md:px-8 py-10 text-center text-sm"
              style={{ color: "#9AA4B2" }}
              data-testid="result-mutation-empty"
            >
              Geen mutaties die aan de filters voldoen.
            </li>
          )}
          {visibleGroups.map((g, i) => {
            const key = `${g.category}-${g.minute}-${i}`;
            const open = !!expanded[key];
            const isGroup = g.items.length > 1;
            const newest = g.items[0];
            return (
              <li
                key={key}
                style={{
                  borderBottom: "1px solid rgba(192,197,206,0.08)",
                }}
                data-testid={`audit-group-${i}`}
              >
                <button
                  type="button"
                  onClick={() => isGroup && toggleGroup(key)}
                  disabled={!isGroup}
                  data-testid={`audit-group-toggle-${i}`}
                  data-open={open ? "true" : "false"}
                  className="w-full grid items-start px-6 md:px-8 py-4 gap-4 md:gap-6 text-left"
                  style={{
                    gridTemplateColumns: "1.4fr 1fr 3.2fr",
                    background: "transparent",
                    border: "none",
                    cursor: isGroup ? "pointer" : "default",
                    color: "inherit",
                  }}
                >
                  <div
                    className="text-[12.5px]"
                    style={{
                      fontFamily:
                        "ui-monospace, 'JetBrains Mono', 'Menlo', monospace",
                      color: "#D4D8DF",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {isGroup
                      ? `${formatAuditDateOnly(newest.timestamp)} · ${g.minute.slice(
                          11,
                          16
                        )}`
                      : formatAuditTs(newest.timestamp)}
                  </div>
                  <div className="flex items-center gap-2">
                    <AuditBadge action={newest.action} />
                    {isGroup && (
                      <span
                        className="text-[10px] tracking-[0.22em] uppercase"
                        style={{ color: "#7FD7E4" }}
                      >
                        {g.items.length} acties
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-white leading-relaxed flex items-start justify-between gap-3">
                    <span className="flex-1 min-w-0">
                      {isGroup
                        ? CATEGORY_LABEL_PLURAL[g.category] ||
                          "Mutaties geregistreerd"
                        : newest.detail}
                    </span>
                    {isGroup &&
                      (open ? (
                        <ChevronDown
                          strokeWidth={1.5}
                          className="w-4 h-4 shrink-0 text-[#7FD7E4]"
                        />
                      ) : (
                        <ChevronRight
                          strokeWidth={1.5}
                          className="w-4 h-4 shrink-0 text-[#7FD7E4]"
                        />
                      ))}
                  </div>
                </button>

                {isGroup && open && (
                  <ul
                    className="bg-[#0F1E33]/40"
                    data-testid={`audit-group-detail-${i}`}
                  >
                    {g.items.map((entry, j) => (
                      <li
                        key={`${entry.timestamp}-${j}`}
                        className="grid items-start px-6 md:px-12 py-2.5 gap-4 md:gap-6"
                        style={{
                          gridTemplateColumns: "1.4fr 1fr 3.2fr",
                          borderTop: "1px solid rgba(192,197,206,0.06)",
                        }}
                      >
                        <div
                          className="text-[11.5px]"
                          style={{
                            fontFamily:
                              "ui-monospace, 'JetBrains Mono', 'Menlo', monospace",
                            color: "#9AA4B2",
                            letterSpacing: "0.02em",
                          }}
                        >
                          {formatAuditTs(entry.timestamp)}
                        </div>
                        <div>
                          <AuditBadge action={entry.action} />
                        </div>
                        <div className="text-[13px] text-[#D4D8DF] leading-relaxed">
                          {entry.detail}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        {hasMore && (
          <div
            className="px-6 md:px-8 py-4"
            style={{ borderTop: "1px solid rgba(192,197,206,0.12)" }}
          >
            <button
              type="button"
              onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
              data-testid="audit-load-more"
              className="w-full inline-flex items-center justify-center gap-2 text-[10px] tracking-[0.22em] uppercase py-2.5 transition-colors"
              style={{
                color: "#7FD7E4",
                border: "1px solid rgba(127,215,228,0.40)",
                background: "rgba(91,192,235,0.06)",
                cursor: "pointer",
              }}
            >
              <Filter strokeWidth={1.25} className="w-3.5 h-3.5" />
              Laad meer acties · {groups.length - visibleCount} resterend
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

// ============================================================================
// StepAuditList — compact per-step log shown inside the Validation Drawer
// ============================================================================
export const StepAuditList = ({ logs, stepNumber }) => {
  const filtered = useMemo(() => {
    if (!stepNumber) return [];
    return logs
      .filter((e) => stepNumberOf(e) === stepNumber)
      .reverse();
  }, [logs, stepNumber]);

  return (
    <section data-testid="drawer-step-log">
      <div className="overline mb-3" style={{ color: "#7FD7E4" }}>
        Stap-specifiek logboek
      </div>
      {filtered.length === 0 ? (
        <p
          className="text-[12.5px] italic"
          style={{ color: "#9AA4B2" }}
          data-testid="drawer-step-log-empty"
        >
          Nog geen geregistreerde mutaties voor deze stap.
        </p>
      ) : (
        <ul
          className="space-y-2"
          data-testid="drawer-step-log-list"
        >
          {filtered.map((entry, i) => (
            <li
              key={`${entry.timestamp}-${i}`}
              className="px-3 py-2.5"
              style={{
                background: "#0F1E33",
                border: "1px solid rgba(192,197,206,0.12)",
              }}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <AuditBadge action={entry.action} />
                <span
                  className="text-[10.5px]"
                  style={{
                    fontFamily:
                      "ui-monospace, 'JetBrains Mono', 'Menlo', monospace",
                    color: "#9AA4B2",
                    letterSpacing: "0.02em",
                  }}
                >
                  {formatAuditTs(entry.timestamp)}
                </span>
              </div>
              <p
                className="text-[12.5px] leading-relaxed"
                style={{ color: "#D4D8DF" }}
              >
                {entry.detail}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// ---------- Tiny presentational atoms ----------
const FilterSelect = ({ testid, value, onChange, options }) => (
  <div className="md:col-span-2 relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full appearance-none bg-transparent text-white text-sm pl-3 pr-8 py-2"
      style={{
        border: "1px solid rgba(127,215,228,0.30)",
        outline: "none",
        fontFamily: "Outfit, sans-serif",
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} style={{ background: "#0F1E33" }}>
          {o.label}
        </option>
      ))}
    </select>
    <ChevronDown
      strokeWidth={1.25}
      className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-[#7FD7E4] pointer-events-none"
      aria-hidden
    />
  </div>
);

const DateInput = ({ testid, value, onChange, placeholder }) => (
  <div className="md:col-span-2 relative">
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      aria-label={placeholder}
      className="w-full bg-transparent text-white text-sm px-3 py-2"
      style={{
        border: "1px solid rgba(127,215,228,0.30)",
        outline: "none",
        fontFamily: "Outfit, sans-serif",
        colorScheme: "dark",
      }}
    />
  </div>
);

export default AuditTrailPanel;
